import { apiClient, authToken } from "./api";

// Auth types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  access_token: string;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  avatarUrl?: string;
  createdAt: string;
  updatedAt: string;
  _count: {
    campaigns: number;
    comments: number;
    responses: number;
  };
}

export interface AdminUserDetail extends AdminUser {
  username?: string;
  bio?: string;
  location?: string;
  website?: string;
  expertise?: string;
  emailNotifications: boolean;
  projectUpdates: boolean;
  newFeedback: boolean;
  validationResults: boolean;
  marketingEmails: boolean;
  emailSharesUsed: number;
  emailSharesLimit: number;
  lastEmailShareDate?: string;
  _count: {
    campaigns: number;
    comments: number;
    responses: number;
    likes: number;
    activities: number;
    notifications: number;
    pricingFeedback: number;
    emailShares: number;
  };
  campaigns: Array<{
    id: string;
    title: string;
    status: string;
    createdAt: string;
    _count: {
      responses: number;
      comments: number;
    };
  }>;
}

export interface UsersResponse {
  users: AdminUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface BulkActionResponse {
  action: string;
  total: number;
  successful: number;
  failed: number;
  results: Array<{
    userId: string;
    success: boolean;
    data?: AdminUser | { message: string };
    error?: string;
  }>;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: "ADMIN" | "CREATOR" | "USER";
  avatarUrl?: string;
  createdAt: string;
  updatedAt: string;
}

// Auth API service
export const authApi = {
  // Login user
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(
      "/auth/login",
      credentials
    );
    authToken.set(response.access_token);
    return response;
  },

  // Register user
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(
      "/auth/register",
      userData
    );
    authToken.set(response.access_token);
    return response;
  },

  // Get current user profile
  async getProfile(): Promise<User> {
    return apiClient.get<User>("/auth/profile");
  },

  // Logout user
  logout(): void {
    authToken.remove();
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return authToken.isAuthenticated();
  },

  // Get current token
  getToken(): string | null {
    return authToken.get();
  },

  // Set token (for OAuth flows)
  setToken(token: string): void {
    authToken.set(token);
  },

  // Verify Google token
  async verifyGoogleToken(token: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>("/auth/google/verify", {
      access_token: token,
    });
    return response;
  },

  // Admin User Management
  async getUsers(
    page: number = 1,
    limit: number = 20,
    search?: string,
    role?: string
  ): Promise<UsersResponse> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (search) params.append("search", search);
    if (role && role !== "all") params.append("role", role);

    return apiClient.get(`/auth/admin/users?${params.toString()}`);
  },

  async getUserById(id: string): Promise<AdminUserDetail> {
    return apiClient.get(`/auth/admin/users/${id}`);
  },

  async updateUserRole(id: string, role: string): Promise<AdminUser> {
    return apiClient.patch(`/auth/admin/users/${id}/role`, { role });
  },

  async updateUserStatus(id: string, isActive: boolean): Promise<AdminUser> {
    return apiClient.patch(`/auth/admin/users/${id}/status`, { isActive });
  },

  async deleteUser(id: string): Promise<{ message: string }> {
    return apiClient.delete(`/auth/admin/users/${id}`);
  },

  async bulkUserAction(
    userIds: string[],
    action: string,
    value?: string | boolean
  ): Promise<BulkActionResponse> {
    return apiClient.post("/auth/admin/users/bulk-action", {
      userIds,
      action,
      value,
    });
  },
};
